import {
  ITemplate,
  TEMPLATE_TYPE,
  buildPageCustomizationCollectionDataUpdateRequests,
  validUUID,
} from '@typedream/data';
import { ELEMENT_NAVBAR } from '@typedream/dream-editor';
import { BaseDreamEditor } from '@typedream/dream-editor/src/types';
import { Button, Text } from '@typedream/ui';
import { replaceNodeChildren, withoutNormalizing } from '@udecode/plate-core';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import Loading from '@/components/Loading';
import LoadingSmall from '@/components/LoadingSmall';
import ModalPreviewTemplate from '@/components/ModalPreviewTemplate';
import { api } from '@/data/api';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import { DreamEditor, DreamValue } from '@/sharedComponents/TextEditor/plate';
import { BLOCK_TYPE } from '@/sharedComponents/TextEditor/types';
import {
  useCreatePageMutation,
  useGetSpaceQuery,
  useGetTemplateQuery,
  useProcessTemplateBeforeUsageMutation,
  useSaveContentMutation,
  useUpdatePageCustomizationMutation,
} from '@/store';
import { PageTemplateType } from '@/types';
import { errToString } from '@/utils/errors';
import TokenContext from '@/utils/jwt/tokenContext';

import {
  isValidMarketPlaceTemplate,
  openTemplateDuplicateLink,
} from '@/features/templates/helpers/marketplaceTemplate.helpers';
import { ArrowRight } from '@phosphor-icons/react';
import { Template } from './Template';

interface PageTemplatesContentProps {
  templates: PageTemplateType[];
  loading: boolean;
  err: any;
  editor: DreamEditor | BaseDreamEditor;
  resetTab: () => void;
}

export function PageTemplatesContent({
  templates,
  loading,
  err,
  editor,
  resetTab,
}: PageTemplatesContentProps) {
  const router = useRouter();
  const [addLoading, setAddLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const { spacePermissionTokenHandler } = useContext(TokenContext);
  const { mutate: updatePageCustomization } = useUpdatePageCustomizationMutation();
  const { data: templateData, isLoading: checking } = useGetTemplateQuery(
    [spaceID, selectedTemplate?.id],
    {
      skip: !selectedTemplate,
    }
  );
  const { data: spaceData } = useGetSpaceQuery([spaceID]);
  const { mutate: createPage } = useCreatePageMutation({ fixedCacheKey: `createPage-${spaceID}` });
  const { mutate: saveContent } = useSaveContentMutation();

  const currentChild = useMemo(
    () => editor.children?.[0]?.children?.filter((c) => c.type === ELEMENT_NAVBAR),
    [editor.children]
  );

  const { mutate: processTemplateBeforeUsage } = useProcessTemplateBeforeUsageMutation();

  const onSelectTemplate = useCallback(async () => {
    if (!editor || !templateData || !spaceID) return;

    setApplyLoading(true);
    try {
      if (isValidMarketPlaceTemplate(selectedTemplate)) {
        openTemplateDuplicateLink(selectedTemplate);
      } else {
        await api().template().use(spaceID, selectedTemplate.id, spacePermissionTokenHandler);
      }

      const updatePageCustomizationRequests = buildPageCustomizationCollectionDataUpdateRequests(
        spaceID,
        (templateData &&
          'customization' in templateData &&
          templateData.customization?.collection_data) ||
          undefined
      );

      let templateContent: DreamValue;
      const publishedContent = templateData.published_content;
      if (Array.isArray(publishedContent)) {
        templateContent = publishedContent;
      } else {
        templateContent = [publishedContent];
      }

      const processedData = await processTemplateBeforeUsage(
        spaceID,
        selectedTemplate.id,
        templateContent
      ).unwrap();

      if (updatePageCustomizationRequests?.length > 0) {
        await updatePageCustomization({
          spaceID,
          pageID,
          customizationUpdateRequests: updatePageCustomizationRequests,
        }).unwrap();
      }

      withoutNormalizing(editor, () => {
        replaceNodeChildren(editor, {
          at: [0],
          nodes: [...currentChild, ...processedData[0].children],
        });
      });

      // reset the states
      setApplyLoading(false);
      setSelectedTemplate(null);
      resetTab();
    } catch (e) {
      setApplyLoading(false);
      toast.error(errToString(e));
    }
  }, [
    editor,
    pageID,
    resetTab,
    spaceID,
    spacePermissionTokenHandler,
    templateData,
    updatePageCustomization,
    currentChild,
  ]);

  const onAddPage = useCallback(async () => {
    if (spaceData) {
      setAddLoading(true);
      setSelectedTemplate(null);
      const routes = spaceData?.routes;

      try {
        // create a new page
        const { page: newPage } = await createPage({
          spaceID: spaceID as string,
          pagePath: [],
          routeAtPath: routes,
        }).unwrap();

        // redirect to the new page
        if (validUUID(newPage?.id) && templateData.published_content?.[0]) {
          // update new page conten
          await saveContent({
            spaceID: spaceData.id,
            pageID: newPage.id,
            newContent: [
              {
                ...templateData.content[0],
                type: BLOCK_TYPE.ROOT,
                children: [...currentChild, ...templateData.published_content[0].children],
              },
            ],
            force: true,
          });

          const spaceSlug = spaceData?.slug;
          router.push(`/s/${spaceSlug}/${spaceData.id}/${newPage?.id}`);

          // reset the states
          resetTab();
          setAddLoading(false);
        }
      } catch (error) {
        setAddLoading(false);
        toast.error(errToString(error));
      }
    }
  }, [createPage, currentChild, editor, saveContent, spaceData, templateData]);

  const renderActions = <T extends TEMPLATE_TYPE>(usedTemplate: ITemplate<T>) => {
    // Check if the template is a valid marketplace template.
    if (isValidMarketPlaceTemplate(usedTemplate) && usedTemplate.type === TEMPLATE_TYPE.PAGE) {
      // If the template is a paid template
      if (usedTemplate.metadata?.price && usedTemplate.metadata?.price > 0) {
        return (
          <Button
            className="bg-gradient-to-bl from-landing-templates-gradient-3 to-landing-templates-gradient-4"
            onClick={onSelectTemplate}
          >
            <Text size="xs" weight="medium" className="text-white">
              {`Purchase for ${Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: usedTemplate.metadata.price_currency,
              }).format(usedTemplate.metadata.price)}`}
            </Text>
            <ArrowRight size={16} color="white" className="ml-1" />
          </Button>
        );
      }

      return (
        <Button
          onClick={onSelectTemplate}
          className="bg-gradient-to-bl from-landing-templates-gradient-3 to-landing-templates-gradient-4"
        >
          <Text size="xs" weight="medium" className="text-white">
            Use Template
          </Text>
          <ArrowRight size={16} color="white" className="ml-1" />
        </Button>
      );
    }

    // Check if the template is marketplace template.
    return (
      <div className="flex gap-2.5 w-full">
        <Button
          variant="outline"
          onClick={onAddPage}
          className="flex-1"
          isDisabled={checking || applyLoading}
          isLoading={addLoading}
        >
          Add as a new page
        </Button>
        <Button
          onClick={onSelectTemplate}
          className="flex-1"
          isDisabled={checking || addLoading}
          isLoading={applyLoading}
        >
          Apply Template
        </Button>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col flex-1 gap-4 h-full overflow-auto pb-8">
        {addLoading && <Loading loading />}
        {loading && (
          <div className="flex items-center justify-center gap-2">
            <LoadingSmall loading={loading} />
            <span className="is-subtitle">Grabbing templates...</span>
          </div>
        )}

        {!loading && templates.length === 0 && (
          <div
            className="flex flex-col items-center justify-start gap-4 pt-8 px-4 text-center"
            style={{ height: 'calc(100% - 52px)' }}
          >
            <Image
              src="/images/no_templates_found.svg"
              alt="no templates found"
              width={160}
              height={160}
            />
            <Text size="md" weight="semibold">
              No templates found
            </Text>
          </div>
        )}

        {err && (
          <span className="with-danger-text-color is-subtitle-size has-fullwidth has-text-centered p-3">
            {err}
          </span>
        )}

        {templates.map((template) => (
          <div key={template.id} style={{ marginLeft: 16, marginRight: 16 }}>
            <Template
              img={template.preview}
              onHover={() => {
                // Do nothing
              }}
              onMouseLeave={() => {
                // Do nothing
              }}
              onSelectTemplate={() => {
                setSelectedTemplate(template);
              }}
            />
          </div>
        ))}
      </div>
      {selectedTemplate && (
        <ModalPreviewTemplate
          templateId={selectedTemplate?.id}
          spaceId={spaceID}
          open
          setopen={() => (!applyLoading ? setSelectedTemplate(null) : null)}
          renderActions={renderActions(selectedTemplate)}
        />
      )}
    </>
  );
}
